/**
 * LegalPageFromStatic — fallback rendering for legal pages when the
 * production server's static-routing layer doesn't auto-resolve
 * `/<path>` to `/<path>/index.html`.
 *
 * What it does:
 *   1. On mount, fetches the canonical static HTML (`/terms/index.html`,
 *      `/privacy/index.html`, `/support/index.html`).
 *   2. Replaces the current document with that HTML so the user (and the
 *      Apple App Store reviewer using a real browser) sees the actual
 *      legal content instead of the SPA shell.
 *   3. Reads <title>, <meta description>, and the canonical <link> from
 *      the static HTML and applies them to the live document while the
 *      fetch is in flight, so SEO crawlers / preview cards stay correct.
 *
 * Why a single React component and not a hand-rolled page per route:
 *   The canonical HTML files in `/public/{terms,privacy,support}/index.html`
 *   are now the SINGLE source of truth. Updating the legal text means
 *   editing one HTML file, not React JSX too.
 *
 * Known limitation: still requires JavaScript. A no-JS curl will return
 * the SPA shell. The real long-term fix is a server-side rewrite from
 * `/terms` to `/terms/index.html` (waiting on Emergent infra).
 */
import React, { useEffect, useState } from "react";

export default function LegalPageFromStatic({ slug }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const staticUrl = `/${slug}/index.html`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(staticUrl, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        if (cancelled) return;

        // Sanity check: the response must actually be the legal HTML, not
        // the SPA shell. If the server returned index.html (catch-all), the
        // <title> would say "TourismIntel | Tourism intelligence, not tourism noise" — bail out.
        if (html.includes("Tourism intelligence, not tourism noise")) {
          throw new Error("static fetch returned SPA shell — server routing misconfig");
        }

        // Replace the entire document with the static HTML. This is the
        // most reliable way to deliver the legal content with its own
        // <head> (title, description, canonical) intact.
        document.open();
        document.write(html);
        document.close();
        // Note: React's reconciler is now disconnected from this document.
        // That's intentional — the legal page is fully self-contained HTML
        // and does not need the SPA runtime.
      } catch (e) {
        if (!cancelled) {
          console.warn(`[LegalPage:${slug}]`, e);
          setError(e.message);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slug, staticUrl]);

  if (loading && !error) {
    return (
      <div
        data-testid={`legal-page-loading-${slug}`}
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fcf8f0",
          color: "#5f5f5f",
          fontFamily: "-apple-system, BlinkMacSystemFont, Inter, system-ui, sans-serif",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  // Fetch failed (offline / static missing). Show a graceful fallback that
  // still gives the user the direct link to the canonical static file.
  return (
    <div
      data-testid={`legal-page-error-${slug}`}
      style={{
        minHeight: "100vh",
        background: "#fcf8f0",
        color: "#0a0a0a",
        fontFamily: "-apple-system, BlinkMacSystemFont, Inter, system-ui, sans-serif",
        padding: "60px 24px",
        textAlign: "center",
      }}
    >
      <div style={{ height: 6, background: "#ffcb00", margin: "-60px -24px 40px" }} />
      <p style={{ color: "#5f5f5f", fontSize: 13, letterSpacing: 2, marginBottom: 12 }}>LEGAL</p>
      <h1 style={{ fontSize: 32, fontWeight: 900, marginBottom: 16 }}>
        Sorry — couldn&apos;t load this page.
      </h1>
      <p style={{ color: "#5f5f5f", marginBottom: 24 }}>
        Please open the canonical version directly:
      </p>
      <a
        href={staticUrl}
        style={{
          color: "#006874",
          fontWeight: 700,
          textDecoration: "none",
          borderBottom: "2px solid #ffcb00",
          paddingBottom: 2,
        }}
      >
        {staticUrl}
      </a>
      <p style={{ marginTop: 40 }}>
        <a href="/" style={{ color: "#5f5f5f", fontSize: 14 }}>
          ← Back to TourismIntel
        </a>
      </p>
    </div>
  );
}
